import { inject } from '@angular/core';
import { Router } from '@angular/router';

import {
  ContentPackageResponse,
  ContentPackageSource,
  HttpContentPackageSource,
  normalizeRelativePackagePath,
  PreviewMetadata,
} from './content-package-source';
import { previewPackageRoot } from './preview-package-root';

export class PreviewContentPackageSource extends HttpContentPackageSource {
  override async loadPreviewMetadata(): Promise<PreviewMetadata | null> {
    const response = await this.readJson<PreviewMetadata>('preview-metadata.json');
    if (response.status === 404) {
      return null;
    }
    if (!response.ok || !response.value) {
      throw new Error(`Preview-Metadaten konnten nicht geladen werden: HTTP ${response.status}`);
    }
    assertValidPreviewMetadata(response.value, this.packageId);
    return response.value;
  }
}

export function createPreviewContentPackageSource(bundleId: string): ContentPackageSource {
  assertValidBundleId(bundleId);
  return new PreviewContentPackageSource(previewPackageRoot(bundleId), bundleId);
}

export function previewContentPackageSourceFactory(): ContentPackageSource {
  const router = inject(Router);
  const navigationUrl = router.getCurrentNavigation()?.finalUrl?.toString() ?? router.url;
  const primarySegments = router.parseUrl(navigationUrl).root.children['primary']?.segments
    .map((segment) => segment.path) ?? [];
  const bundleId = primarySegments[0] === 'redaktionsvorschau'
    ? primarySegments[1] ?? ''
    : '';
  try {
    return createPreviewContentPackageSource(bundleId);
  } catch (error) {
    return new RejectedContentPackageSource(bundleId, error);
  }
}

export function assertValidBundleId(bundleId: string): void {
  if (!/^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/.test(bundleId)) {
    throw new Error(`Ungültige Preview-Bundle-ID "${bundleId}".`);
  }
}

function assertValidPreviewMetadata(metadata: PreviewMetadata, expectedBundleId: string): void {
  if (
    metadata.schemaVersion !== 1
    || metadata.bundleId !== expectedBundleId
    || !/^[a-f0-9]{64}$/.test(metadata.bundleSha)
    || metadata.bundleSha !== metadata.digest
    || !Array.isArray(metadata.files)
    || !Array.isArray(metadata.documents)
  ) {
    throw new Error('Preview-Metadaten sind ungültig oder gehören zu einem anderen Bundle.');
  }
  if (metadata.repository !== undefined && !/^[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+$/.test(metadata.repository)) {
    throw new Error('Preview-Repository ist ungültig.');
  }
  for (const [label, sha] of [
    ['Content-Commit', metadata.contentCommitSha],
    ['Base-Commit', metadata.baseCommitSha],
  ] as const) {
    if (sha !== undefined && !/^[0-9a-f]{40}$/.test(sha)) {
      throw new Error(`${label} in den Preview-Metadaten ist ungültig.`);
    }
  }
  for (const file of metadata.files) {
    normalizeRelativePackagePath(file.path);
    if (!/^[a-f0-9]{64}$/.test(file.sha256) || file.bytes < 0) {
      throw new Error(`Ungültiger Dateinachweis für "${file.path}".`);
    }
  }
  for (const document of metadata.documents) {
    normalizeRelativePackagePath(document.packageFile);
    normalizeRelativePackagePath(document.sourceFile);
    if (document.blockIds.some((blockId) => !/^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/.test(blockId))) {
      throw new Error(`Ungültige Block-ID in "${document.sourceFile}".`);
    }
    if (
      !Array.isArray(document.blocks)
      || document.blocks.some((block) =>
        !document.blockIds.includes(block.id)
        || typeof block.runtimeId !== 'string'
        || !block.runtimeId.trim())
    ) {
      throw new Error(`Ungültige Blockzuordnung in "${document.sourceFile}".`);
    }
  }
  if (metadata.diagnostics !== undefined && !Array.isArray(metadata.diagnostics)) {
    throw new Error('Preview-Diagnosen sind ungültig.');
  }
  for (const diagnostic of metadata.diagnostics ?? []) {
    normalizeRelativePackagePath(diagnostic.sourceFile);
    if (diagnostic.severity !== 'error'
      || !diagnostic.courseId
      || typeof diagnostic.message !== 'string'
      || !diagnostic.message.trim()
      || (diagnostic.line !== null && (!Number.isSafeInteger(diagnostic.line) || diagnostic.line < 1))) {
      throw new Error(`Ungültige Preview-Diagnose für "${diagnostic.sourceFile}".`);
    }
  }
  if (metadata.changes !== undefined) {
    if (!Array.isArray(metadata.changes.documents) || !Array.isArray(metadata.changes.unassignedDocuments)) {
      throw new Error('Preview-Änderungsdaten sind ungültig.');
    }
    for (const change of metadata.changes.documents) {
      normalizeRelativePackagePath(change.sourceFile);
      if (!change.courseId || !change.moduleId || !change.lessonId || !Array.isArray(change.reasons)) {
        throw new Error(`Ungültige Änderungszuordnung für "${change.sourceFile}".`);
      }
    }
    for (const document of metadata.changes.unassignedDocuments) {
      normalizeRelativePackagePath(document.sourceFile);
      if (typeof document.message !== 'string' || !document.message.trim()) {
        throw new Error(`Ungültiger Diagnoseeintrag für "${document.sourceFile}".`);
      }
    }
  }
}

class RejectedContentPackageSource implements ContentPackageSource {
  readonly packageId: string;
  private readonly message: string;

  constructor(bundleId: string, error: unknown) {
    this.packageId = bundleId;
    this.message = error instanceof Error ? error.message : String(error);
  }

  readJson<T>(): Promise<ContentPackageResponse<T>> {
    return Promise.reject(new Error(this.message));
  }

  assetBaseUrl(): string {
    return '/invalid-preview-package/';
  }

  loadPreviewMetadata(): Promise<PreviewMetadata | null> {
    return Promise.reject(new Error(this.message));
  }
}
